import type { AssetClass, CandlePoint, FinLabSDK, VolumePoint } from '@sdk';
import { ASSET_CLASSES, guessAssetClass } from '@sdk';
import { field, isoToday, yearsAgo } from './dom';

type CoreState = {
  symbol: string;
  assetClass: AssetClass | 'auto';
  from: string;
  to: string;
};

const DEFAULT: CoreState = {
  symbol: 'AAPL',
  assetClass: 'auto',
  from: yearsAgo(2),
  to: isoToday(),
};

export function mountOverview(sdk: FinLabSDK): () => void {
  const state = sdk.persist.load<CoreState>('core.overview.v2', DEFAULT);
  const root = document.createElement('div');
  root.className = 'panel-inner';
  root.dataset.module = 'core';

  root.innerHTML = `
    <div class="panel-head">
      <h2>Overview</h2>
      <p>Core price candles via TradingView <em>Lightweight Charts</em> (self-hosted — not the embed widget; that cannot plot custom leverage series). Open <strong>Leverage overlay</strong> in the left rail for ×1 / ×2 / ×3 wealth paths on the same host.</p>
    </div>
    <form class="toolbar" id="core-form">
      <label>Symbol ${field('symbol', state.symbol, 'AAPL')}</label>
      <label>Asset class
        <select name="assetClass">
          <option value="auto">auto</option>
          ${ASSET_CLASSES.map((c) => `<option value="${c}" ${state.assetClass === c ? 'selected' : ''}>${c}</option>`).join('')}
        </select>
      </label>
      <label>From <input type="date" name="from" value="${state.from}" /></label>
      <label>To <input type="date" name="to" value="${state.to}" /></label>
      <button type="submit" class="btn primary">Load</button>
    </form>
    <p class="hint" id="core-hint"></p>
    <div class="quote-row" id="core-quote"></div>
  `;

  sdk.shell.mountPanel(root);
  const form = root.querySelector('#core-form') as HTMLFormElement;
  const hint = root.querySelector('#core-hint') as HTMLElement;
  const quoteEl = root.querySelector('#core-quote') as HTMLElement;

  async function load() {
    const fd = new FormData(form);
    const symbol = String(fd.get('symbol') || '').trim().toUpperCase();
    const assetPick = String(fd.get('assetClass') || 'auto') as CoreState['assetClass'];
    const from = String(fd.get('from') || state.from);
    const to = String(fd.get('to') || state.to);
    if (!symbol) return;

    const assetClass = assetPick === 'auto' ? guessAssetClass(symbol) : assetPick;
    const next: CoreState = { symbol, assetClass: assetPick, from, to };
    sdk.persist.save('core.overview.v2', next);
    sdk.shell.setStatus(`loading ${symbol}…`, 'idle');
    sdk.debug?.('info', 'overview', `load ${symbol} ${from}→${to} (${assetClass})`);
    hint.textContent = '';
    quoteEl.textContent = '';

    try {
      const [hist, quote] = await Promise.all([
        sdk.data.history(symbol, { from, to, assetClass }),
        sdk.data.quote(symbol, { assetClass }).catch(() => null),
      ]);
      const rows = hist.rows.filter((r) => r.close != null);
      if (!rows.length) {
        sdk.chart.clear();
        hint.textContent = `No bars for ${symbol} in ${from} → ${to}.`;
        sdk.shell.setStatus('no data', 'warn');
        return;
      }

      const usedAdj = rows.some((r) => r.adjustedClose != null);
      const missingAdj = rows.some((r) => r.adjustedClose == null && r.close != null);
      if (missingAdj) {
        hint.textContent =
          'adjustedClose is missing on some (or all) bars — showing raw close. Splits will lie. Prefer a range where EODHD answered.';
      } else if (usedAdj) {
        hint.textContent = `${rows.length} bars · adjusted close · source mix from aggregator waterfall`;
      }

      const candles: CandlePoint[] = rows.map((r) => {
        const close = (r.adjustedClose ?? r.close) as number;
        const open = r.open ?? close;
        const high = r.high ?? Math.max(open, close);
        const low = r.low ?? Math.min(open, close);
        return { time: r.tradeDate, open, high, low, close };
      });
      const volume: VolumePoint[] = rows.map((r, i) => {
        const prev = i > 0 ? (rows[i - 1].adjustedClose ?? rows[i - 1].close) : r.close;
        const up = (r.close ?? 0) >= (prev ?? 0);
        return {
          time: r.tradeDate,
          value: r.volume ?? 0,
          color: up ? 'rgba(62,229,139,0.35)' : 'rgba(255,93,115,0.35)',
        };
      });
      sdk.chart.setCandles(candles, volume);
      sdk.debug?.('info', 'overview', `candles ${symbol}`, { n: candles.length });

      if (quote?.close != null) {
        quoteEl.innerHTML = `
          <span class="k">${symbol}</span>
          <span class="v">${quote.close.toFixed(2)}</span>
          <span class="m">${quote.tradeDate || ''} · ${quote.source || ''}${quote.cacheHit ? ' · cache' : ''}</span>
        `;
      }
      sdk.shell.setStatus(`${symbol} · ${rows.length} bars`, 'ok');
    } catch (err) {
      sdk.chart.clear();
      const message = err instanceof Error ? err.message : String(err);
      sdk.debug?.('error', 'overview', message);
      hint.textContent = message;
      sdk.shell.setStatus('load failed', 'err');
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void load();
  });
  void load();

  return () => {
    form.replaceChildren();
  };
}
